import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader2, Trophy, Sparkles, ExternalLink, Lightbulb, Gift, Star } from "lucide-react";
import { toast } from "sonner";

export default function SponsoredPlay() {
  const { gameId } = useParams();
  const { lang } = useT();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", consent_marketing: false });
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/games/sponsored/${gameId}`);
        setGame(data);
      } catch {
        toast.error(lang === "fr" ? "Jeu introuvable" : "Game not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [gameId, lang]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E5276A]" /></div>;
  if (!game) return <div className="py-24 text-center text-[#8B95A1]">{lang === "fr" ? "Jeu introuvable" : "Game not found"}</div>;

  const gameType = game.game_type || "quiz";

  const onFinish = (s, t) => {
    setScore(s);
    setTotal(t);
    setDone(true);
    if (t > 0 && s / t >= 0.7) setShowLeadForm(true);
  };

  const submitLead = async (e) => {
    e?.preventDefault();
    try {
      const { data } = await api.post(`/games/sponsored/${gameId}/result`, {
        score,
        max: total,
        name: lead.name,
        email: lead.email,
        consent_marketing: lead.consent_marketing,
      });
      setSubmitted(data);
      setShowLeadForm(false);
      if (data.lead_created) {
        toast.success(lang === "fr" ? "Merci ! Vos coordonnées ont été transmises au domaine." : "Thanks! Your details were shared with the estate.");
      }
    } catch {
      toast.error(lang === "fr" ? "Soumission échouée" : "Submission failed");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 md:p-8" data-testid="sponsored-play">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <span className="chip-pink">
              {gameType === "treasure_hunt" ? (lang === "fr" ? "Chasse au trésor" : "Treasure hunt") :
               gameType === "memory" ? "Memory" :
               (lang === "fr" ? "Quiz sponsorisé" : "Sponsored quiz")}
            </span>
            <h1 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-2">{game.title}</h1>
            {game.producer_name && (
              <p className="text-xs text-[#8B95A1] mt-1">{lang === "fr" ? "par" : "by"} <b className="text-[#1F3A5F]">{game.producer_name}</b></p>
            )}
          </div>
        </div>

        {!done && gameType === "quiz" && <QuizPlay game={game} lang={lang} onFinish={onFinish} />}
        {!done && gameType === "treasure_hunt" && <TreasureHuntPlay game={game} lang={lang} onFinish={onFinish} />}
        {!done && gameType === "memory" && <MemoryPlay game={game} lang={lang} onFinish={onFinish} />}

        {done && (
          <div className="mt-6 text-center" data-testid="sp-result">
            <Trophy className="w-10 h-10 text-[#E5276A] mx-auto" />
            <p className="mt-2 font-display text-2xl text-[#1F3A5F]">{score}/{total}</p>
            <p className="text-sm text-[#4A5568]">{total ? Math.round((score / total) * 100) : 0}%</p>
            {total > 0 && score / total >= 0.7 && !submitted && (
              <p className="mt-3 text-sm text-[#1F3A5F]">{lang === "fr" ? "Excellent ! Recevez un cadeau du domaine en échange de vos coordonnées." : "Excellent! Get a gift from the estate in exchange for your details."}</p>
            )}

            {submitted?.won && submitted?.reward && (
              <RewardPanel reward={submitted.reward} lang={lang} />
            )}
            {submitted?.lead_created && submitted?.sponsor_link && (
              <a href={submitted.sponsor_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]" data-testid="sp-sponsor-link">
                {lang === "fr" ? "Découvrir le domaine" : "Visit the estate"} <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        )}

        {showLeadForm && !submitted && (
          <form onSubmit={submitLead} className="mt-6 rounded-2xl bg-[#FBF7F3] p-5 space-y-3" data-testid="sp-lead-form">
            <div className="flex items-center gap-2 text-[#1F3A5F]">
              <Sparkles className="w-4 h-4 text-[#E5276A]" />
              <h3 className="font-bold">{lang === "fr" ? "Recevez le cadeau du domaine" : "Receive the estate's gift"}</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="sp-lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} required placeholder={lang === "fr" ? "Votre nom" : "Your name"} className="px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
              <input data-testid="sp-lead-email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required type="email" placeholder="email@example.com" className="px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]" />
            </div>
            <label className="flex items-start gap-2 text-xs text-[#4A5568]">
              <input data-testid="sp-lead-consent" type="checkbox" checked={lead.consent_marketing} onChange={(e) => setLead({ ...lead, consent_marketing: e.target.checked })} required className="mt-0.5 accent-[#E5276A]" />
              <span>{lang === "fr"
                ? `J'accepte que ${game.producer_name || "le domaine sponsor"} m'envoie des communications commerciales. Je peux me désabonner à tout moment.`
                : `I agree to receive marketing communications from ${game.producer_name || "the sponsor"}. I can unsubscribe at any time.`}</span>
            </label>
            <button data-testid="sp-lead-submit" type="submit" className="px-5 py-2 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
              {lang === "fr" ? "Recevoir mon cadeau" : "Claim my gift"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── Quiz ─────────────────────────
function QuizPlay({ game, lang, onFinish }) {
  const questions = game.questions || [];
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const total = questions.length;
  const q = questions[idx];
  if (!q) return null;
  const select = (i) => setAnswers({ ...answers, [idx]: i });
  const next = () => {
    if (idx + 1 < total) setIdx(idx + 1);
    else {
      let s = 0;
      questions.forEach((qq, i) => { if (answers[i] === qq.answer_index) s++; });
      onFinish(s, total);
    }
  };
  return (
    <div className="mt-6">
      <div className="text-xs font-bold uppercase tracking-wider text-[#E5276A]">{lang === "fr" ? "Question" : "Question"} {idx + 1}/{total}</div>
      <h2 className="mt-2 font-display text-lg md:text-xl text-[#1F3A5F]">{q.q}</h2>
      <div className="mt-4 grid gap-2">
        {q.choices.map((c, i) => (
          <button key={i} data-testid={`sp-choice-${i}`} onClick={() => select(i)}
            className={`text-left px-4 py-3 rounded-xl border transition-colors ${
              answers[idx] === i ? "border-[#E5276A] bg-[#FCE4ED] text-[#1F3A5F]" : "border-[#1F3A5F]/15 hover:border-[#E5276A] text-[#1F3A5F] hover:bg-[#FBF7F3]"
            }`}>
            <span className="font-bold mr-2 text-[#E5276A]">{String.fromCharCode(65 + i)}.</span> {c}
          </button>
        ))}
      </div>
      <button disabled={answers[idx] == null} data-testid="sp-next" onClick={next}
        className="mt-5 px-5 py-2 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870] disabled:opacity-50">
        {idx + 1 < total ? (lang === "fr" ? "Question suivante" : "Next") : (lang === "fr" ? "Terminer" : "Finish")}
      </button>
    </div>
  );
}

// ───────────────────────── Treasure Hunt ─────────────────────────
function TreasureHuntPlay({ game, lang, onFinish }) {
  const steps = game.steps || [];
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState(null); // "ok" | "ko"

  const step = steps[idx];
  if (!step) return null;

  const matches = (typed, expected, aliases) => {
    const n = (s) => (s || "").toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const t = n(typed);
    if (!t) return false;
    const candidates = [n(expected), ...(aliases || []).map(n)];
    return candidates.some((c) => c && (c === t || t.includes(c) || c.includes(t)));
  };

  const submit = (e) => {
    e?.preventDefault();
    const ok = matches(input, step.answer, step.answer_aliases || []);
    setFeedback(ok ? "ok" : "ko");
    const newScore = score + (ok ? 1 : 0);
    setTimeout(() => {
      setFeedback(null);
      setShowHint(false);
      setInput("");
      if (idx + 1 < steps.length) {
        setIdx(idx + 1);
        setScore(newScore);
      } else {
        onFinish(newScore, steps.length);
      }
    }, 1200);
  };

  return (
    <div className="mt-6" data-testid="sp-treasure">
      <div className="text-xs font-bold uppercase tracking-wider text-[#E5276A]">{lang === "fr" ? "Étape" : "Step"} {idx + 1}/{steps.length}</div>
      <h2 className="mt-2 font-display text-lg md:text-xl text-[#1F3A5F]">{step.title}</h2>
      <p className="mt-3 text-[#1F3A5F] leading-relaxed">{step.riddle}</p>
      {showHint && step.hint && (
        <p className="mt-2 text-sm text-[#4A5568] italic bg-[#FBF7F3] rounded-lg p-3 inline-flex items-start gap-2"><Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {step.hint}</p>
      )}
      <form onSubmit={submit} className="mt-5 flex flex-wrap items-center gap-2">
        <input
          data-testid="sp-treasure-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={lang === "fr" ? "Votre réponse…" : "Your answer…"}
          className="flex-1 min-w-[200px] px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
        />
        <button type="submit" data-testid="sp-treasure-submit" disabled={!input.trim() || feedback}
          className="px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
          {feedback === "ok" ? (lang === "fr" ? "Bravo !" : "Bravo!") : feedback === "ko" ? (lang === "fr" ? "Manqué" : "Missed") : (lang === "fr" ? "Valider" : "Submit")}
        </button>
        {step.hint && !showHint && (
          <button type="button" onClick={() => setShowHint(true)} data-testid="sp-treasure-hint"
            className="text-xs underline text-[#4A5568] hover:text-[#E5276A]">
            <Lightbulb className="inline w-3.5 h-3.5 mr-1" /> {lang === "fr" ? "Indice" : "Hint"}
          </button>
        )}
      </form>
      {feedback === "ok" && step.reward_text && (
        <p className="mt-3 text-sm text-green-700">{step.reward_text}</p>
      )}
    </div>
  );
}

// ───────────────────────── Memory ─────────────────────────
function MemoryPlay({ game, lang, onFinish }) {
  const pairs = game.pairs || [];
  const [matched, setMatched] = useState(() => new Set());
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [feedback, setFeedback] = useState(null); // "ok" | "ko"
  const [locked, setLocked] = useState(false);

  // Stable shuffled right column (computed once with lazy init)
  const [rightShuffled] = useState(() => {
    const arr = pairs.map((p, i) => ({ ...p, _i: i }));
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });

  const handleLeftClick = (i) => {
    if (locked || matched.has(i)) return;
    setSelectedLeft(i);
    if (selectedRight != null) tryResolve(i, selectedRight);
  };
  const handleRightClick = (i) => {
    if (locked || matched.has(i)) return;
    setSelectedRight(i);
    if (selectedLeft != null) tryResolve(selectedLeft, i);
  };
  const tryResolve = (l, r) => {
    setLocked(true);
    const ok = l === r;
    setFeedback(ok ? "ok" : "ko");
    setTimeout(() => {
      setLocked(false);
      setFeedback(null);
      setSelectedLeft(null);
      setSelectedRight(null);
      if (ok) {
        setMatched((prev) => {
          const next = new Set(prev);
          next.add(l);
          if (next.size === pairs.length) onFinish(next.size, pairs.length);
          return next;
        });
      }
    }, 700);
  };

  return (
    <div className="mt-6" data-testid="sp-memory">
      <p className="text-sm text-[#4A5568]">{lang === "fr" ? "Associez chaque élément de gauche à sa paire de droite." : "Match each left element with its right pair."}</p>
      <p className="mt-1 text-xs text-[#8B95A1]">{matched.size}/{pairs.length} {lang === "fr" ? "paires" : "pairs"}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          {pairs.map((p, i) => {
            const isMatched = matched.has(i);
            const isSelected = selectedLeft === i;
            return (
              <button
                key={i}
                disabled={isMatched || locked}
                data-testid={`sp-memory-left-${i}`}
                onClick={() => handleLeftClick(i)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors ${
                  isMatched ? "border-green-300 bg-green-50 text-green-800 opacity-70" :
                  isSelected ? (feedback === "ko" ? "border-red-400 bg-red-50" : "border-[#E5276A] bg-[#FCE4ED]") :
                  "border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                }`}>{p.left}</button>
            );
          })}
        </div>
        <div className="space-y-2">
          {rightShuffled.map((p) => {
            const isMatched = matched.has(p._i);
            const isSelected = selectedRight === p._i;
            return (
              <button
                key={p._i}
                disabled={isMatched || locked}
                data-testid={`sp-memory-right-${p._i}`}
                onClick={() => handleRightClick(p._i)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors ${
                  isMatched ? "border-green-300 bg-green-50 text-green-800 opacity-70" :
                  isSelected ? (feedback === "ko" ? "border-red-400 bg-red-50" : "border-[#E5276A] bg-[#FCE4ED]") :
                  "border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                }`}>{p.right}</button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Reward panel ─────────────────────────
function RewardPanel({ reward, lang }) {
  if (!reward || reward.kind === "none") return null;
  return (
    <div className="mt-5 rounded-2xl bg-gradient-to-br from-[#FCE4ED] to-white border border-[#E5276A]/30 p-5 text-left max-w-md mx-auto" data-testid="sp-reward">
      <div className="flex items-center gap-2 text-[#E5276A]">
        <Gift className="w-5 h-5" />
        <h3 className="font-display text-lg text-[#1F3A5F]">
          {reward.kind === "voucher" ? (lang === "fr" ? "Votre code promo" : "Your voucher code") :
           reward.kind === "discount" ? (lang === "fr" ? "Votre remise" : "Your discount") :
           reward.kind === "tasting" ? (lang === "fr" ? "Votre dégustation" : "Your tasting") :
           reward.kind === "google_review" ? (lang === "fr" ? "Aidez le domaine" : "Help the estate") :
           (lang === "fr" ? "Votre récompense" : "Your reward")}
        </h3>
      </div>
      {reward.description && <p className="mt-2 text-sm text-[#1F3A5F]">{reward.description}</p>}
      {reward.code && (
        <div className="mt-3 inline-block px-3 py-1.5 rounded-lg bg-[#1F3A5F] text-white font-mono text-base tracking-wider" data-testid="sp-reward-code">
          {reward.code}
        </div>
      )}
      {reward.value && reward.kind !== "voucher" && (
        <div className="mt-2 text-sm font-bold text-[#1F3A5F]">{reward.value}</div>
      )}
      {reward.redeem_url && (
        <a href={reward.redeem_url} target="_blank" rel="noreferrer" data-testid="sp-reward-redeem"
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
          {lang === "fr" ? "Utiliser maintenant" : "Redeem now"} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {reward.kind === "google_review" && reward.google_review_url && (
        <a href={reward.google_review_url} target="_blank" rel="noreferrer" data-testid="sp-reward-google"
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">
          <Star className="w-3.5 h-3.5" /> {lang === "fr" ? "Laisser un avis Google" : "Leave a Google review"}
        </a>
      )}
    </div>
  );
}
